import React, { useMemo } from 'react';
import Navigation from './Navigation';
import StatCard from './StatCard';
import ActionCard from './ActionCard';
import { useAllTransactions } from '../hooks/useAllTransactions';
import { toMMDDYYYY } from '../utils/dateFormat';
import { formatCurrency } from '../utils/formatCurrency';
import '../styles/dashboard.css';

function Dashboard({ onLogout, onNavigate, userName, people }) {
  const allTransactions = useAllTransactions(people);

  // Gerçek verilerden istatistikleri hesapla
  const stats = useMemo(() => {
    if (!people || people.length === 0) {
      return {
        totalPeople: 0,
        totalStores: 0,
        totalBalance: 0,
        totalTransactions: 0
      };
    }

    let totalStores = 0;
    let totalBalance = 0;

    people.forEach(person => {
      if (person.stores && Array.isArray(person.stores)) {
        person.stores.forEach(store => {
          if (store.email && store.email.trim() !== '') {
            totalStores++;
          }
          totalBalance += store.bal || 0;
        });
      }
    });

    const expensesTotal = people.reduce((sum, p) => sum + (p.expenses || []).reduce((s, e) => s + (e.amount || 0), 0), 0);

    return {
      totalPeople: people.length,
      totalStores,
      totalBalance: totalBalance - expensesTotal,
      totalTransactions: allTransactions.length,
      totalExpenses: expensesTotal
    };
  }, [people, allTransactions]);

  const statCards = [
    { 
      icon: '👥', 
      value: stats.totalPeople, 
      label: 'Total People', 
      change: 'Active', 
      color: 'blue' 
    },
    { 
      icon: '🏪', 
      value: stats.totalStores, 
      label: 'Active Stores', 
      change: 'With email', 
      color: 'green' 
    },
    { 
      icon: '💰', 
      value: `$${stats.totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
      label: 'Total Balance', 
      change: 'After expenses', 
      color: 'orange' 
    },
    { 
      icon: '📊', 
      value: stats.totalTransactions, 
      label: 'Transactions', 
      change: 'All time', 
      color: 'purple' 
    }
  ];

  // Son işlemleri al (Firestore + manual birleşik)
  const recentTransactions = useMemo(() => {
    return allTransactions.slice(0, 5);
  }, [allTransactions]);

  // Son harcamalar (person-level) - en son eklenen en üstte (createdAt)
  const recentExpenses = useMemo(() => {
    return (people || [])
      .flatMap(p => (p.expenses || []).map(e => ({ ...e, personName: p.name })))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 5);
  }, [people]);

  const actions = [
    { 
      icon: '👥', 
      title: 'Manage People', 
      desc: `View and manage ${stats.totalPeople} people`,
      onClick: () => onNavigate('stores')
    },
    { 
      icon: '⚙️', 
      title: 'Email Integration', 
      desc: 'Configure automatic payment tracking',
      onClick: () => onNavigate('settings')
    },
    { 
      icon: '📈', 
      title: 'View Analytics', 
      desc: 'Detailed reports and insights',
      onClick: () => onNavigate('analytics')
    }
  ];

  return (
    <div>
      <Navigation 
        currentPage="dashboard" 
        onNavigate={onNavigate}
        onLogout={onLogout}
        userName={userName}
      />
      
      <div className="container">
        <div className="welcome-section">
          <h1 className="welcome-title">Welcome back, {userName || 'Admin'}! 👋</h1>
          <p className="welcome-subtitle">Here's what's happening with your stores today.</p>
        </div>

        <div className="stats-grid">
          {statCards.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>

        <div className="dashboard-grid">
          <div className="dashboard-section">
            <div className="section-header">
              <div className="section-title">Recent Transactions</div>
              <div className="view-all" onClick={() => onNavigate('analytics')}>View All →</div>
            </div>

            <div className="transactions-list">
              {recentExpenses.length > 0 && (
                <div className="expenses-preview" style={{ marginBottom: 16 }}>
                  <div className="section-subtitle" style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Recent Expenses</div>
                  {recentExpenses.map(e => (
                    <div key={e.id} className="transaction-item" style={{ opacity: 0.9 }}>
                      <div className="transaction-icon">➖</div>
                      <div className="transaction-details">
                        <div className="transaction-title">{e.description || 'Expense'}</div>
                        <div className="transaction-meta">{e.personName} • {toMMDDYYYY(e.date) || e.date}</div>
                      </div>
                      <div className="transaction-amount expense">-${formatCurrency(Math.abs(e.amount || 0))}</div>
                    </div>
                  ))}
                </div>
              )}
              {recentTransactions.length === 0 && recentExpenses.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <div className="empty-text">No transactions yet</div>
                  <div className="empty-desc">Start by adding a person and their stores</div>
                </div>
              ) : recentTransactions.length > 0 ? (
                recentTransactions.map((transaction, index) => (
                  <div key={transaction.id || index} className="transaction-item">
                    <div className="transaction-icon">
                      {transaction.type === 'income' ? '📈' : '📉'}
                    </div>
                    <div className="transaction-details">
                      <div className="transaction-title">
                        {transaction.desc || transaction.description || 'Transaction'}
                      </div>
                      <div className="transaction-meta">
                        {transaction.storeName || transaction.storeCode} • {transaction.personName}
                        {transaction.date && ` • ${toMMDDYYYY(transaction.date) || transaction.date}`}
                      </div>
                    </div>
                    <div className={`transaction-amount ${transaction.type}`}>
                      {transaction.type === 'income' ? '+' : '-'}${formatCurrency(Math.abs(transaction.amount))}
                    </div>
                  </div>
                ))
              ) : null}
            </div>
          </div>

          <div className="dashboard-section">
            <div className="section-header">
              <div className="section-title">Quick Actions</div>
            </div>

            <div className="quick-actions-list">
              {actions.map((action, index) => (
                <ActionCard key={index} {...action} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;